import React, { useState, useEffect, useCallback } from 'react';
import { deviceTypeLabel } from '../../../utils/terminology';
import { Fingerprint, Plus, Trash2, Loader, CheckCircle, AlertCircle, ChevronRight, MapPin, RefreshCw } from 'lucide-react';
import { PersonDetailData } from '../index';
import { fingerprintService, PersonFingerprint, FINGER_LABELS } from '../../../services/fingerprintService';
import {
  deviceCommunicationService,
  DeviceInfo,
  DeviceOperations,
  CommunicationMode,
  uuidToDeterministicControlIDId,
} from '../../../services/deviceCommunication';
import { DeviceIntegration } from '../../../integrations/devices';
import { api, fetchWithAuth } from '../../../services/api';
import { cn } from '../../../utils/cn';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { isElectron } from '../../../utils/platform';
import { isFingerprintDevice, classifyCaptureError, IDBIO_DEVICE_ID } from './fingerprintRules';
import { reportDeviceSyncOutcome } from '../../../utils/deviceSyncOutcome';

interface BiometriaTabProps {
  person: PersonDetailData;
  customerId: string;
  darkMode?: boolean;
}

interface ControlIDDevice {
  id: string;
  name: string;
  vendor: string;
  ip_address?: string;
  local_ip_address?: string;
  port?: string;
  username: string;
  password: string;
  direction?: 'entrada' | 'saida' | 'passagem';
}

interface FingerprintZoneDevice {
  id: string;
  name: string;
  device_type: string;
  manufacturer?: string;
  model?: string;
}

interface FingerprintZone {
  id: string;
  name: string;
  devices: FingerprintZoneDevice[];
}

type WizardStep = 'list' | 'select-device' | 'idbio-port' | 'select-finger' | 'capturing' | 'success' | 'error';

// Type-safe accessor for IDBioPro API (bypasses stale d.ts cache)
interface IdbioAPI {
  listPorts: () => Promise<{ ports: string[] }>;
  connect: (portPath: string) => Promise<{ success: boolean; version?: string; serial?: string; model?: string; error?: string }>;
  disconnect: () => Promise<{ success: boolean }>;
  enroll: (enrollId: number) => Promise<{ success: boolean; template?: string; code?: number; error?: string; conflicting_reader_id?: number }>;
  cancelEnroll: () => Promise<{ success: boolean }>;
  deleteReaderUser: (readerId: number) => Promise<{ success: boolean; error?: string }>;
  onEnrollProgress: (callback: (data: { count: number }) => void) => () => void;
}
const getIdbioAPI = (): IdbioAPI | undefined => (window.electronAPI as any)?.idbio as IdbioAPI | undefined;

// Sentinel device used when user chooses IDBioPro USB scanner
const IDBIO_DEVICE: ControlIDDevice = {
  id: IDBIO_DEVICE_ID,
  name: 'IDBioPro USB',
  vendor: 'idbio',
  ip_address: '',
  username: '',
  password: '',
};

const FINGER_OPTIONS = Object.entries(FINGER_LABELS).map(([k, v]) => ({
  value: parseInt(k, 10),
  label: v,
}));

export const BiometriaTab: React.FC<BiometriaTabProps> = ({ person, customerId, darkMode = false }) => {
  const [fingerprints, setFingerprints] = useState<PersonFingerprint[]>([]);
  const [devices, setDevices] = useState<ControlIDDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [fpToDelete, setFpToDelete] = useState<PersonFingerprint | null>(null);

  // Fingerprint device zones (for sync section)
  const [fpZones, setFpZones] = useState<FingerprintZone[]>([]);
  const [syncingDeviceId, setSyncingDeviceId] = useState<string | null>(null);
  const [syncingAll, setSyncingAll] = useState(false);

  // Wizard state
  const [step, setStep] = useState<WizardStep>('list');
  const [selectedDevice, setSelectedDevice] = useState<ControlIDDevice | null>(null);
  const [selectedFinger, setSelectedFinger] = useState<number>(0);
  const [captureError, setCaptureError] = useState<string>('');
  const [capturedWithTemplate, setCapturedWithTemplate] = useState(false);
  const [conflictingReaderId, setConflictingReaderId] = useState<number | null>(null);

  // IDBioPro USB state
  const [idbioPorts, setIdbioPorts] = useState<string[]>([]);
  const [idbioDetectedPorts, setIdbioDetectedPorts] = useState<string[]>([]); // ports found at wizard open
  const [idbioSelectedPort, setIdbioSelectedPort] = useState('');
  const [idbioConnecting, setIdbioConnecting] = useState(false);
  const [idbioConnected, setIdbioConnected] = useState(false);
  const [idbioDeviceInfo, setIdbioDeviceInfo] = useState<{ version: string; serial: string; model: string } | null>(null);
  const [idbioConnectError, setIdbioConnectError] = useState('');
  const [captureCount, setCaptureCount] = useState(0); // 0..2 intermediate captures during IDBioPro enrollment
  // After IDBioPro success — track which iDFlex devices are being enrolled
  const [idbioSuccessMode, setIdbioSuccessMode] = useState(false);
  const [enrollingDeviceId, setEnrollingDeviceId] = useState<string | null>(null);
  const [enrolledDeviceIds, setEnrolledDeviceIds] = useState<string[]>([]);

  // Fully cancel any ongoing enrollment and close the serial port, then await it.
  const disconnectIdbio = async () => {
    const idbio = getIdbioAPI();
    if (!idbio) return;
    try { await idbio.cancelEnroll(); } catch { /* ignore */ }
    try { await idbio.disconnect(); } catch { /* ignore */ }
    setIdbioConnected(false);
    setIdbioDeviceInfo(null);
  };

  const load = useCallback(async () => {
    try {
      const data = await fingerprintService.list(customerId, person.id);
      setFingerprints(data);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao carregar digitais');
    } finally {
      setLoading(false);
    }
  }, [customerId, person.id]);

  const loadDevices = useCallback(async () => {
    try {
      const res = await api.get(`/iot-devices/customer/${customerId}`);
      const all: any[] = res.data?.devices ?? res.data ?? [];
      const controlIDDevices: ControlIDDevice[] = all
        .filter(d =>
          d.manufacturer?.toLowerCase() === 'controlid' &&
          d.model?.toLowerCase() === 'idflex'
        )
        .map(d => ({
          id: d.id,
          name: d.name,
          vendor: 'controlid',
          ip_address: d.ip_address,
          local_ip_address: d.local_ip_address,
          port: d.port,
          username: d.username ?? d.config?.username ?? '',
          password: d.password ?? d.config?.password ?? '',
          direction: d.direction,
        }));
      setDevices(controlIDDevices);
    } catch {
      // non-critical — wizard will show empty list
    }
  }, [customerId]);

  const loadFpZones = useCallback(async () => {
    if (!customerId || !person.id) return;
    try {
      const res = await api.get(`/customers/${customerId}/people/${person.id}/effective-access-zones`);
      const zones: any[] = res.data.zones || [];
      const filtered: FingerprintZone[] = zones
        .map(z => ({
          id: z.id,
          name: z.name,
          devices: (z.devices || []).filter(isFingerprintDevice),
        }))
        .filter(z => z.devices.length > 0);
      setFpZones(filtered);
    } catch {
      setFpZones([]);
    }
  }, [customerId, person.id]);

  useEffect(() => {
    load();
    loadDevices();
    loadFpZones();
  }, [load, loadDevices, loadFpZones]);

  // Subscribe to IDBioPro intermediate capture progress while in capturing step
  useEffect(() => {
    if (step !== 'capturing' || selectedDevice?.id !== IDBIO_DEVICE_ID) return;
    const idbio = getIdbioAPI();
    if (!idbio?.onEnrollProgress) return;
    const unsub = idbio.onEnrollProgress(({ count }) => setCaptureCount(count));
    return unsub;
  }, [step, selectedDevice]);



  // Fetch full device details (credentials + IPs) needed for direct communication
  const fetchDeviceInfo = async (deviceId: string): Promise<DeviceInfo | null> => {
    try {
      const res = await api.get(`/iot-devices/${deviceId}`);
      const d = res.data;
      return {
        id: d.id,
        ip_address: d.ip_address,
        local_ip_address: d.local_ip_address,
        username: d.username || '',
        password: d.password || '',
        vendor: 'controlid',
        port: d.port,
      };
    } catch {
      return null;
    }
  };

  // Pre-fetch photo with auth token and return as data URI (avoids 401 in direct sync)
  const fetchPhotoDataUri = async (): Promise<string | undefined> => {
    const rawUrl = person.identity_photo_url || person.profile_picture_url;
    if (!rawUrl) return undefined;
    try {
      const fullUrl = rawUrl.startsWith('/')
        ? `https://access.panther.com.br${rawUrl}`
        : rawUrl;
      const resp = await fetchWithAuth(fullUrl);
      if (!resp.ok) return undefined;
      const blob = await resp.blob();
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch {
      return undefined;
    }
  };

  const buildPersonSyncData = (photoDataUri?: string, targetDeviceIds?: string[]) => ({
    person_id: person.id,
    first_name: person.first_name,
    last_name: person.last_name,
    cpf: person.cpf,
    identity_photo_url: photoDataUri || person.identity_photo_url,
    profile_picture_url: photoDataUri || person.profile_picture_url,
    isAllowed: true,
    rfid_tags: [],
    vehicle_plates: [],
    // Pass ALL fingerprint templates (IDBioPro and iDFlex) — both are ControlID format.
    // Always push to all devices: after vincular a new UUID user is created on the device,
    // so the template must be pushed even to the source device.
    fingerprint_templates: fingerprints
      .filter(fp => !!fp.template_b64)
      .map(fp => ({ templateB64: fp.template_b64! })),
  });

  const needsReEnrollmentForDevice = (deviceId?: string): boolean => {
    if (fingerprints.length === 0) return false;
    if (fingerprints.some(fp => !!fp.template_b64)) return false;
    if (deviceId && fingerprints.some(fp => fp.enrolled_device_id === deviceId)) return false;
    return true;
  };

  const showSyncToast = (deviceName: string, sourceLabel?: string, deviceId?: string) => {
    if (needsReEnrollmentForDevice(deviceId)) {
      toast(`Usuário sincronizado com "${deviceName}"${sourceLabel ? ` (${sourceLabel})` : ''}.\nDigitais precisam ser recadastradas no dispositivo.`, { duration: 7000 });
    } else {
      toast.success(`Sincronizado com "${deviceName}"${sourceLabel ? ` (${sourceLabel})` : ''}`);
    }
  };

  const handleSyncDevice = async (deviceId: string, deviceName: string) => {
    setSyncingDeviceId(deviceId);
    try {
      const isElectronApp = typeof window !== 'undefined' && !!window.electronAPI;

      if (isElectronApp) {
        const [deviceInfo, photoDataUri] = await Promise.all([
          fetchDeviceInfo(deviceId),
          fetchPhotoDataUri(),
        ]);
        if (deviceInfo) {
          const hasDirectAccess = !!(deviceInfo.local_ip_address || deviceInfo.ip_address);
          if (hasDirectAccess) {
            toast.loading(`Conectando diretamente com "${deviceName}"...`, { id: `sync-fp-${deviceId}` });
          }
          const result = await DeviceIntegration.instance.execute(
            deviceInfo, DeviceOperations.SYNC_PERSON, buildPersonSyncData(photoDataUri, [deviceId]), CommunicationMode.HYBRID
          );
          toast.dismiss(`sync-fp-${deviceId}`);
          if (result.success) {
            const sourceLabel =
              result.source === 'local' ? 'local' :
              result.source === 'public' ? 'público' : 'nuvem';
            const fpFailed: number = result.data?.fpFailed ?? 0;
            const fpPushed: number = result.data?.fpPushed ?? 0;
            const fpLastError: string | undefined = result.data?.fpLastError;
            if (fpFailed > 0) {
              console.warn(`[BiometriaTab] ${fpFailed} template(s) failed to push. Last error:`, fpLastError);
              toast(`Dados sincronizados com "${deviceName}" (${sourceLabel}), mas ${fpFailed} digital(is) não pôde(ram) ser enviada(s) ao leitor.\n\nErro: ${fpLastError || 'desconhecido'}`, { duration: 10000 });
            } else if (fpPushed > 0) {
              toast.success(`${fpPushed} digital(is) sincronizada(s) com "${deviceName}" (${sourceLabel})`);
            } else {
              showSyncToast(deviceName, sourceLabel, deviceId);
            }
            // Audit trail + reconciliation ledger (no device call — Electron already did it).
            await reportDeviceSyncOutcome({
              customerId,
              personId: person.id,
              deviceId,
              credentialType: 'fingerprint',
              success: true,
              source: result.source,
              durationMs: result.latencyMs,
            });
            loadFpZones();
            return;
          }
        }
        // ARCHITECTURE RULE: inside Electron, device sync is executed LOCALLY only —
        // never delegated to the cloud. A local failure is surfaced as a failure.
        toast.error(`Falha ao sincronizar "${deviceName}" — dispositivo não respondeu localmente`);
        return;
      }

      // Web browser (non-Electron): the backend executes the sync via cloud.
      await api.post(`/customers/${customerId}/people/${person.id}/sync`, { device_ids: [deviceId] });
      showSyncToast(deviceName, undefined, deviceId);
      loadFpZones();
    } catch (err: any) {
      toast.error(err.response?.data?.error || `Erro ao sincronizar "${deviceName}"`);
    } finally {
      setSyncingDeviceId(null);
    }
  };

  const handleSyncAll = async () => {
    const allDevices = fpZones.flatMap(z => z.devices);
    if (allDevices.length === 0) return;
    setSyncingAll(true);
    try {
      const isElectronApp = typeof window !== 'undefined' && !!window.electronAPI;

      if (isElectronApp) {
        const [deviceDetails, photoDataUri] = await Promise.all([
          Promise.all(allDevices.map(d => fetchDeviceInfo(d.id))),
          fetchPhotoDataUri(),
        ]);
        const validDevices = deviceDetails.filter((d): d is DeviceInfo => d !== null);

        if (validDevices.length > 0) {
          const hasDirectDevices = validDevices.some(d => !!(d.local_ip_address || d.ip_address));
          if (hasDirectDevices) {
            toast.loading(`Tentando conexão direta com ${validDevices.length} leitor(es)...`, { id: 'sync-fp-all' });
          }
          const results = await Promise.all(
            validDevices.map(deviceInfo =>
              DeviceIntegration.instance.execute(
                deviceInfo, DeviceOperations.SYNC_PERSON, buildPersonSyncData(photoDataUri, [deviceInfo.id]), CommunicationMode.HYBRID
              )
            )
          );
          toast.dismiss('sync-fp-all');
          const successes = results.filter(r => r.success);
          // Audit trail + reconciliation ledger, for failures too (no device call —
          // Electron already did it).
          await Promise.all(
            results.map((r, i) =>
              reportDeviceSyncOutcome({
                customerId,
                personId: person.id,
                deviceId: validDevices[i].id,
                credentialType: 'fingerprint',
                success: r.success,
                source: r.source,
                durationMs: r.latencyMs,
                errorMessage: r.success ? undefined : r.error,
              })
            )
          );
          const failedCount = allDevices.length - successes.length;
          if (successes.length > 0) {
            const localCount = successes.filter(r => r.source === 'local').length;
            const publicCount = successes.filter(r => r.source === 'public').length;
            const cloudCount = successes.filter(r => r.source === 'cloud').length;
            const parts: string[] = [];
            if (localCount > 0) parts.push(`${localCount} local`);
            if (publicCount > 0) parts.push(`${publicCount} público`);
            if (cloudCount > 0) parts.push(`${cloudCount} nuvem`);
            // Count template push results across all successful syncs
            const totalFpPushed = successes.reduce((s, r) => s + (r.data?.fpPushed ?? 0), 0);
            const totalFpFailed = successes.reduce((s, r) => s + (r.data?.fpFailed ?? 0), 0);
            const fpLastError = successes.find(r => r.data?.fpLastError)?.data?.fpLastError;
            if (totalFpFailed > 0) {
              console.warn(`[BiometriaTab] ${totalFpFailed} template(s) failed across all devices. Last error:`, fpLastError);
              toast(`${successes.length} leitor(es) sincronizado(s) (${parts.join(', ')}), mas ${totalFpFailed} digital(is) não pôde(ram) ser enviada(s).\n\nErro: ${fpLastError || 'desconhecido'}`, { duration: 10000 });
            } else if (totalFpPushed > 0) {
              toast.success(`${successes.length} leitor(es) sincronizado(s) com ${totalFpPushed} digital(is) (${parts.join(', ')})`);
            } else if (needsReEnrollmentForDevice()) {
              toast(`${successes.length} leitor(es) sincronizado(s) (${parts.join(', ')}).\nDigitais precisam ser recadastradas no dispositivo.`, { duration: 7000 });
            } else if (failedCount > 0) {
              toast(`Sincronizado parcialmente: ${successes.length}/${allDevices.length} leitor(es) biométrico(s) (${parts.join(', ')})`);
            } else {
              toast.success(`${successes.length} leitor(es) biométrico(s) sincronizado(s) (${parts.join(', ')})`);
            }
            loadFpZones();
            return;
          }
        }
        // ARCHITECTURE RULE: inside Electron, device sync is executed LOCALLY only —
        // never delegated to the cloud. A local failure is surfaced as a failure.
        toast.error('Falha ao sincronizar leitores biométricos — nenhum dispositivo respondeu localmente');
        return;
      }

      // Web browser (non-Electron): the backend executes the sync via cloud.
      const allIds = allDevices.map(d => d.id);
      const res = await api.post(`/customers/${customerId}/people/${person.id}/sync`, { device_ids: allIds });
      const synced = res.data?.devices_synced || 0;
      const failed: any[] = res.data?.failed_devices || [];
      const allCloudAreEnrolled = allDevices.every(d => fingerprints.some(fp => fp.enrolled_device_id === d.id));
      if (needsReEnrollmentForDevice() && !allCloudAreEnrolled) {
        toast(`Sincronizado com ${synced} leitor(es) biométrico(s).\nDigitais precisam ser recadastradas no dispositivo.`, { duration: 7000 });
      } else if (failed.length === 0) {
        toast.success(`Sincronizado com ${synced} leitor(es) biométrico(s) (nuvem)`);
      } else if (synced > 0) {
        toast(`Sincronizado parcialmente: ${synced}/${allIds.length} (nuvem)`);
      } else {
        toast.error('Falha ao sincronizar leitores biométricos');
      }
      loadFpZones();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Erro ao sincronizar biometria');
    } finally {
      setSyncingAll(false);
    }
  };

  const handleDelete = async (fp: PersonFingerprint) => {
    setFpToDelete(null);
    setDeletingId(fp.id);
    try {
      await fingerprintService.deleteFingerprint(customerId, person.id, fp.id);
      toast.success('Digital excluída com sucesso');
      setFingerprints(prev => prev.filter(f => f.id !== fp.id));
    } catch (err: any) {
      toast.error(err.message || 'Erro ao excluir digital');
    } finally {
      setDeletingId(null);
    }
  };

  const startWizard = async () => {
    // Ensure any previous IDBioPro session is fully closed before starting a new one
    await disconnectIdbio();
    setSelectedDevice(null);
    setSelectedFinger(0);
    setCaptureError('');
    setCapturedWithTemplate(false);
    setCaptureCount(0);
    setConflictingReaderId(null);
    setIdbioPorts([]);
    setIdbioDetectedPorts([]);
    setIdbioSelectedPort('');
    setIdbioConnected(false);
    setIdbioDeviceInfo(null);
    setIdbioConnectError('');
    setIdbioSuccessMode(false);
    setEnrollingDeviceId(null);
    setEnrolledDeviceIds([]);
    setStep('select-device');
    // Scan for USB ports so we know whether to show the IDBioPro option
    const idbio = getIdbioAPI();
    if (idbio) {
      const res = await idbio.listPorts();
      setIdbioDetectedPorts(res.ports);
    }
  };

  const enterIdbioPorts = async () => {
    setSelectedDevice(IDBIO_DEVICE);
    setIdbioConnected(false);
    setIdbioDeviceInfo(null);
    setIdbioConnectError('');
    setIdbioPorts(idbioDetectedPorts);
    // Auto-connect when exactly one port is available — skip the port selection screen
    if (idbioDetectedPorts.length === 1) {
      const port = idbioDetectedPorts[0];
      setIdbioSelectedPort(port);
      setIdbioConnecting(true);
      try {
        const res = await getIdbioAPI()!.connect(port);
        if (res.success) {
          setIdbioConnected(true);
          setIdbioDeviceInfo({ version: res.version || '', serial: res.serial || '', model: res.model || '' });
          setIdbioConnecting(false);
          setStep('select-finger');
          return;
        }
        setIdbioConnectError(res.error || 'Falha ao conectar');
      } catch (e: any) {
        setIdbioConnectError(e.message || 'Erro inesperado');
      }
      setIdbioConnecting(false);
    } else {
      setIdbioSelectedPort('');
    }
    setStep('idbio-port');
  };

  const handleIdbioConnect = async () => {
    if (!idbioSelectedPort) return;
    setIdbioConnecting(true);
    setIdbioConnectError('');
    try {
      const res = await getIdbioAPI()!.connect(idbioSelectedPort);
      if (res.success) {
        setIdbioConnected(true);
        setIdbioDeviceInfo({ version: res.version || '', serial: res.serial || '', model: res.model || '' });
      } else {
        setIdbioConnectError(res.error || 'Falha ao conectar');
      }
    } catch (e: any) {
      setIdbioConnectError(e.message || 'Erro inesperado');
    } finally {
      setIdbioConnecting(false);
    }
  };

  const handleCapture = async () => {
    if (!selectedDevice) return;
    setCaptureCount(0);
    setStep('capturing');
    setCaptureError('');

    try {
      // ── IDBioPro USB path ────────────────────────────────────────────────
      if (selectedDevice.id === IDBIO_DEVICE_ID) {
        const enrollId = Math.floor(Date.now() / 1000) % 0x7FFFFFFF;
        const result = await getIdbioAPI()!.enroll(enrollId);
        if (!result.success) {
          // Reset the sensor so it's not stuck in a busy state for the next attempt
          getIdbioAPI()?.cancelEnroll().catch(() => {});
          setCaptureError(result.error || 'Erro ao capturar digital');
          setConflictingReaderId(result.conflicting_reader_id ?? null);
          setStep('error');
          return;
        }
        await fingerprintService.saveTemplate(customerId, person.id, {
          fingerType: selectedFinger,
          fingerLabel: FINGER_LABELS[selectedFinger],
          templateB64: result.template || '',
          enrolledDeviceId: IDBIO_DEVICE_ID,
        });
        // Clean up the reader's internal DB — it's a capture-only device,
        // stale entries cause false "duplicate fingerprint" errors on future enrollments
        getIdbioAPI()?.deleteReaderUser(enrollId).catch(() => {});
        await load();
        setCapturedWithTemplate(true);
        setIdbioSuccessMode(true);
        setEnrolledDeviceIds([]);
        setStep('success');
        // Disconnect the USB device after successful enrollment
        getIdbioAPI()?.disconnect().catch(() => {});
        return;
      }

      if (isElectron()) {
        // ── Electron direct path ──────────────────────────────────────────────
        // Talk directly to the device on the local network.
        const deviceInfo = {
          id: selectedDevice.id,
          name: selectedDevice.name,
          vendor: 'controlid' as const,
          ip_address: selectedDevice.ip_address,
          local_ip_address: selectedDevice.local_ip_address,
          port: selectedDevice.port,
          username: selectedDevice.username,
          password: selectedDevice.password,
          direction: selectedDevice.direction,
        };

        const result = await deviceCommunicationService.enrollFingerprint(
          deviceInfo,
          person.id,
          selectedFinger,
          CommunicationMode.DIRECT,
          [person.first_name, person.last_name].filter(Boolean).join(' ') || person.id.slice(0, 8),
        );

        console.log('[BiometriaTab] enrollFingerprint result:', result);

        if (result.error) {
          setCaptureError(result.error);
          setStep('error');
          return;
        }

        // Enrollment succeeded — always save to backend so it appears in the list
        // Pass empty template if we couldn't retrieve it (fingerprint is still on device)
        const hasTemplate = !!result.templateB64;
        await fingerprintService.saveTemplate(customerId, person.id, {
          fingerType: selectedFinger,
          fingerLabel: FINGER_LABELS[selectedFinger],
          templateB64: result.templateB64 || '',
          enrolledDeviceId: selectedDevice.id,
        });

        // Auto-create during enrollment only sets minimal user fields (no area_id/department).
        // Immediately sync full person data so department is applied without the user having
        // to manually hit "Sincronizar". We use DIRECT mode — modify_objects preserves the
        // fingerprint just enrolled on the device.
        try {
          const photoDataUri = await fetchPhotoDataUri();
          await DeviceIntegration.instance.execute(
            deviceInfo, DeviceOperations.SYNC_PERSON, buildPersonSyncData(photoDataUri, [selectedDevice.id]), CommunicationMode.DIRECT
          );
        } catch {
          // Non-critical — department will be corrected on next manual sync
        }

        await load();
        setCapturedWithTemplate(hasTemplate);
        setStep('success');
        return;
      }

      // ── Cloud path (browser only) ─────────────────────────────────────────
      await fingerprintService.cloudEnroll(customerId, person.id, {
        deviceId: selectedDevice.id,
        fingerType: selectedFinger,
        fingerLabel: FINGER_LABELS[selectedFinger],
      });
      await load();
      setStep('success');
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Erro ao capturar digital';
      setCaptureError(msg);
      setStep('error');
    }
  };

  const card = darkMode
    ? 'bg-gray-800 border-gray-700'
    : 'bg-white border-gray-200';

  const text = darkMode ? 'text-gray-100' : 'text-gray-900';
  const subtext = darkMode ? 'text-gray-400' : 'text-gray-500';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className={cn('w-6 h-6 animate-spin', subtext)} />
      </div>
    );
  }

  // ── Wizard: select device ─────────────────────────────────────────────────
  if (step === 'select-device') {
    return (
      <div className="space-y-4 p-1">
        <div className="flex items-center gap-2 mb-2">
          <button onClick={() => setStep('list')} className={cn('text-sm underline', subtext)}>← Voltar</button>
          <h3 className={cn('font-medium', text)}>Selecione o dispositivo de captura</h3>
        </div>

        {/* IDBioPro USB option (Electron only) */}
        {isElectron() && idbioDetectedPorts.length > 0 && (
          <div className="mb-1">
            <p className={cn('text-xs font-semibold uppercase tracking-wide mb-1', subtext)}>Leitor USB</p>
            <button
              onClick={enterIdbioPorts}
              className={cn(
                'w-full flex items-center justify-between px-4 py-3 rounded-lg border text-left transition-colors',
                card,
                'hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20',
              )}
            >
              <div>
                <span className={cn('font-medium text-sm', text)}>IDBioPro USB</span>
                <p className={cn('text-xs', subtext)}>Leitor USB conectado neste computador</p>
              </div>
              <ChevronRight className={cn('w-4 h-4', subtext)} />
            </button>
          </div>
        )}

        {/* Network devices (ControlID iDFlex) */}
        {devices.length > 0 && (
          <div>
            <p className={cn('text-xs font-semibold uppercase tracking-wide mb-1', subtext)}>Leitores de Rede</p>
            <ul className="space-y-2">
              {devices.map(d => (
                <li key={d.id}>
                  <button
                    onClick={() => { setSelectedDevice(d); setStep('select-finger'); }}
                    className={cn(
                      'w-full flex items-center justify-between px-4 py-3 rounded-lg border text-left transition-colors',
                      card,
                      'hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20',
                    )}
                  >
                    <span className={cn('font-medium text-sm', text)}>{d.name}</span>
                    <ChevronRight className={cn('w-4 h-4', subtext)} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {devices.length === 0 && idbioDetectedPorts.length === 0 && (
          <p className={cn('text-sm', subtext)}>Nenhum dispositivo disponível.</p>
        )}
      </div>
    );
  }

  // ── Wizard: IDBioPro port selection ───────────────────────────────────────
  if (step === 'idbio-port') {
    return (
      <div className="space-y-4 p-1">
        <div className="flex items-center gap-2 mb-2">
          <button onClick={async () => { await disconnectIdbio(); setStep('select-device'); }} className={cn('text-sm underline', subtext)}>← Voltar</button>
          <h3 className={cn('font-medium', text)}>Conectar IDBioPro USB</h3>
        </div>

        {idbioConnected && idbioDeviceInfo ? (
          <div className="space-y-3">
            <div className={cn('p-3 rounded-lg border', darkMode ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200')}>
              <p className={cn('text-sm font-medium', darkMode ? 'text-green-400' : 'text-green-700')}>
                Conectado: {idbioDeviceInfo.model || 'IDBioPro'}
              </p>
              <p className={cn('text-xs mt-0.5', subtext)}>
                Serial: {idbioDeviceInfo.serial} · Firmware: {idbioDeviceInfo.version}
              </p>
            </div>
            <button
              onClick={() => setStep('select-finger')}
              className="w-full py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
            >
              Continuar →
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {idbioPorts.length === 0 ? (
              <p className={cn('text-sm', subtext)}>Nenhum leitor USB detectado. Verifique se o IDBioPro está conectado.</p>
            ) : (
              <div>
                <p className={cn('text-xs mb-2', subtext)}>Selecione a porta serial:</p>
                <ul className="space-y-1">
                  {idbioPorts.map(port => (
                    <li key={port}>
                      <button
                        onClick={() => setIdbioSelectedPort(port)}
                        className={cn(
                          'w-full px-3 py-2 rounded-lg border text-sm text-left transition-colors font-mono',
                          idbioSelectedPort === port
                            ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                            : cn(card, text, 'hover:border-blue-400'),
                        )}
                      >
                        {port}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {idbioConnectError && (
              <p className="text-sm text-red-500">{idbioConnectError}</p>
            )}

            <div className="flex gap-2">
              <button
                onClick={async () => {
                  const res = await (getIdbioAPI()?.listPorts() ?? { ports: [] });
                  setIdbioPorts(res.ports);
                  if (res.ports.length === 1) setIdbioSelectedPort(res.ports[0]);
                }}
                className={cn('px-3 py-2 rounded-lg border text-sm font-medium transition-colors', card, text)}
              >
                Atualizar
              </button>
              <button
                onClick={handleIdbioConnect}
                disabled={!idbioSelectedPort || idbioConnecting}
                className="flex-1 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium transition-colors"
              >
                {idbioConnecting ? 'Conectando...' : 'Conectar'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Wizard: select finger ─────────────────────────────────────────────────
  if (step === 'select-finger') {
    return (
      <div className="space-y-4 p-1">
        <div className="flex items-center gap-2 mb-2">
          <button onClick={async () => { await disconnectIdbio(); setStep('select-device'); }} className={cn('text-sm underline', subtext)}>← Voltar</button>
          <h3 className={cn('font-medium', text)}>Selecione o dedo</h3>
        </div>
        <ul className="grid grid-cols-2 gap-2">
          {FINGER_OPTIONS.map(f => (
            <li key={f.value}>
              <button
                onClick={() => setSelectedFinger(f.value)}
                className={cn(
                  'w-full px-3 py-2 rounded-lg border text-sm text-left transition-colors',
                  selectedFinger === f.value
                    ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                    : cn(card, text, 'hover:border-blue-400'),
                )}
              >
                {f.label}
              </button>
            </li>
          ))}
        </ul>
        <button
          onClick={handleCapture}
          className="w-full py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
        >
          Iniciar Captura
        </button>
      </div>
    );
  }

  // ── Wizard: capturing ─────────────────────────────────────────────────────
  if (step === 'capturing') {
    const isIdbio = selectedDevice?.id === IDBIO_DEVICE_ID;
    return (
      <div className="flex flex-col items-center justify-center py-14 gap-6">
        {isIdbio ? (
          <>
            {/* Pulsing fingerprint scanner indicator */}
            <div className="relative flex items-center justify-center">
              <div className="absolute w-28 h-28 rounded-full bg-blue-500/10 animate-ping" style={{ animationDuration: '2s' }} />
              <div className="absolute w-22 h-22 rounded-full bg-blue-500/15 animate-pulse" />
              <div className={cn(
                'relative w-20 h-20 rounded-full flex items-center justify-center border-2',
                darkMode ? 'bg-blue-900/30 border-blue-600' : 'bg-blue-50 border-blue-300',
              )}>
                <Fingerprint className="w-10 h-10 text-blue-500" />
              </div>
            </div>
            <div className={cn('text-center', text)}>
              <p className="font-semibold text-base">Pressione o dedo 3 vezes</p>
              <p className={cn('text-sm mt-1', subtext)}>Aguarde o beep do dispositivo entre cada pressão</p>
            </div>
          </>
        ) : (
          <>
            <Loader className="w-8 h-8 animate-spin text-blue-500" />
            <div className={cn('text-center', text)}>
              <p className="font-medium">Aguardando captura...</p>
              <p className={cn('text-sm mt-1', subtext)}>
                Vá ao dispositivo <strong>{selectedDevice?.name}</strong> e coloque o dedo 3 vezes no leitor
              </p>
            </div>
          </>
        )}
        <button
          onClick={async () => {
            if (isIdbio) await disconnectIdbio();
            setStep('list');
          }}
          className={cn('px-5 py-2 rounded-lg border text-sm font-medium', card, text)}
        >
          Cancelar
        </button>
      </div>
    );
  }

  // ── Wizard: success ───────────────────────────────────────────────────────
  if (step === 'success') {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <CheckCircle className="w-10 h-10 text-green-500" />
        <div className={cn('text-center max-w-xs', text)}>
          <p className="font-medium">Digital capturada com sucesso!</p>
          <p className={cn('text-sm mt-1', subtext)}>{FINGER_LABELS[selectedFinger]}</p>
          {capturedWithTemplate ? (
            <p className={cn('text-xs mt-2 text-green-600 dark:text-green-400')}>
              Template salvo — sincronize com os leitores pelo botão "Sincronizar Todos".
            </p>
          ) : (
            <p className={cn('text-xs mt-2 text-amber-600 dark:text-amber-400')}>
              Digital cadastrada no leitor. Para outros leitores, sincronize pelo botão abaixo.
            </p>
          )}
        </div>
        <button
          onClick={() => setStep('list')}
          className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
        >
          Ver digitais
        </button>
      </div>
    );
  }

  // ── Wizard: error ─────────────────────────────────────────────────────────
  if (step === 'error') {
    // See ./fingerprintRules for why reader id 0 needs an explicit null check.
    const { isGenericFallback, isDuplicateFingerprint, canForceReenroll } = classifyCaptureError({
      deviceId: selectedDevice?.id,
      captureError,
      conflictingReaderId,
    });
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <AlertCircle className="w-10 h-10 text-red-500" />
        <div className={cn('text-center max-w-xs', text)}>
          {isGenericFallback ? (
            <p className="font-medium">Erro ao capturar digital</p>
          ) : (
            <>
              <p className="font-medium text-red-600 dark:text-red-400">{captureError}</p>
              <p className={cn('text-xs mt-1', subtext)}>Erro ao capturar digital</p>
            </>
          )}
          {canForceReenroll && (
            <p className={cn('text-xs mt-2 px-3 py-2 rounded-lg', darkMode ? 'bg-orange-900/30 text-orange-300' : 'bg-orange-50 text-orange-700')}>
              O leitor tem esta digital registrada internamente. Clique em <strong>Forçar recadastro</strong> para remover o registro antigo e tentar novamente.
            </p>
          )}
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          {canForceReenroll && (
            <button
              onClick={async () => {
                const api = getIdbioAPI();
                if (!api) return;
                await api.deleteReaderUser(conflictingReaderId!);
                setConflictingReaderId(null);
                handleCapture();
              }}
              className="px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium"
            >
              Forçar recadastro
            </button>
          )}
          <button
            onClick={() => { setConflictingReaderId(null); setStep('select-finger'); }}
            className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
          >
            Tentar novamente
          </button>
          <button
            onClick={async () => {
              if (selectedDevice?.id === IDBIO_DEVICE_ID) await disconnectIdbio();
              setConflictingReaderId(null);
              setStep('list');
            }}
            className={cn('px-4 py-2 rounded-lg border text-sm font-medium', card, text)}
          >
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  // ── List view (default) ───────────────────────────────────────────────────
  const isBusy = syncingAll || syncingDeviceId !== null;
  const allFpCount = fpZones.reduce((s, z) => s + z.devices.length, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <button
          onClick={startWizard}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Capturar
        </button>
      </div>

      {fingerprints.length === 0 ? (
        <div className={cn('flex flex-col items-center justify-center py-10 gap-3 rounded-xl border', card)}>
          <Fingerprint className={cn('w-10 h-10', subtext)} />
          <p className={cn('text-sm', subtext)}>Nenhuma digital cadastrada</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {fingerprints.map(fp => (
            <li
              key={fp.id}
              className={cn('flex items-center justify-between px-4 py-3 rounded-xl border', card)}
            >
              <div className="flex items-center gap-3">
                <Fingerprint className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <div>
                  <div className="flex items-center gap-2">
                    <p className={cn('text-sm font-medium', text)}>{fp.finger_label || FINGER_LABELS[fp.finger_type] || `Dedo ${fp.finger_type}`}</p>
                    {!fp.template_b64 && (
                      <span className={cn(
                        'text-xs px-1.5 py-0.5 rounded font-medium',
                        darkMode ? 'bg-amber-900/40 text-amber-400' : 'bg-amber-100 text-amber-700'
                      )} title="Template biométrico não extraído — funciona apenas no leitor onde foi cadastrado">
                        leitor local
                      </span>
                    )}
                  </div>
                  <p className={cn('text-xs', subtext)}>
                    {fp.enrolled_at
                      ? format(parseISO(fp.enrolled_at), "dd/MM/yyyy", { locale: ptBR })
                      : format(parseISO(fp.created_at), "dd/MM/yyyy", { locale: ptBR })}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setFpToDelete(fp)}
                disabled={deletingId === fp.id}
                className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-40"
              >
                {deletingId === fp.id
                  ? <Loader className="w-4 h-4 animate-spin" />
                  : <Trash2 className="w-4 h-4" />}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* ── Delete confirmation modal ────────────────────────────────────── */}
      {fpToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className={cn('w-80 rounded-2xl border shadow-xl p-6 space-y-4', card)}>
            <div className="flex items-center gap-3">
              <div className={cn(
                'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
                darkMode ? 'bg-red-900/40' : 'bg-red-100'
              )}>
                <Trash2 className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className={cn('font-semibold text-sm', text)}>Excluir digital</p>
                <p className={cn('text-xs', subtext)}>{fpToDelete.finger_label || FINGER_LABELS[fpToDelete.finger_type]}</p>
              </div>
            </div>
            <p className={cn('text-sm', subtext)}>
              Tem certeza? Esta ação não pode ser desfeita.
            </p>
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => setFpToDelete(null)}
                className={cn('flex-1 py-2 rounded-lg border text-sm font-medium transition-colors', card, text)}
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(fpToDelete)}
                className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Fingerprint device sync section ─────────────────────────────── */}
      {fpZones.length > 0 && (
        <div className="pt-2">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Fingerprint className="w-4 h-4 text-blue-500" />
              <h4 className={cn('text-sm font-medium', text)}>Leitores Biométricos com Acesso</h4>
              <span className={cn(
                'px-2 py-0.5 rounded-full text-xs font-medium',
                darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'
              )}>
                {allFpCount}
              </span>
            </div>
            <button
              onClick={handleSyncAll}
              disabled={isBusy}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                isBusy
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              )}
            >
              <RefreshCw className={cn('w-3 h-3', syncingAll && 'animate-spin')} />
              Sincronizar Todos
            </button>
          </div>

          <div className="space-y-3">
            {fpZones.map(zone => (
              <div
                key={zone.id}
                className={cn('rounded-lg border', card)}
              >
                <div className={cn(
                  'flex items-center gap-2 px-3 py-2 border-b',
                  darkMode ? 'border-gray-700' : 'border-gray-100'
                )}>
                  <MapPin className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                  <span className={cn('text-xs font-semibold flex-1', text)}>{zone.name}</span>
                  <span className={cn(
                    'text-xs px-1.5 py-0.5 rounded',
                    darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'
                  )}>
                    {zone.devices.length} {zone.devices.length === 1 ? 'leitor' : 'leitores'}
                  </span>
                </div>

                <div className={cn('divide-y', darkMode ? 'divide-gray-800' : 'divide-gray-100')}>
                  {zone.devices.map(device => {
                    const isSyncingThis = syncingDeviceId === device.id;
                    return (
                      <div key={device.id} className="flex items-center gap-3 px-3 py-2">
                        <Fingerprint className={cn('w-4 h-4 flex-shrink-0', darkMode ? 'text-blue-400' : 'text-blue-500')} />
                        <div className="flex-1 min-w-0">
                          <p className={cn('text-sm font-medium truncate', text)}>{device.name}</p>
                          <p className={cn('text-xs', subtext)}>{device.model || deviceTypeLabel(device.device_type)}</p>
                        </div>
                        <button
                          onClick={() => handleSyncDevice(device.id, device.name)}
                          disabled={isSyncingThis || isBusy}
                          title="Sincronizar digitais neste leitor"
                          className={cn(
                            'p-1.5 rounded-md transition-colors',
                            isSyncingThis || isBusy
                              ? 'text-gray-300 cursor-not-allowed'
                              : darkMode
                              ? 'text-gray-400 hover:text-blue-400 hover:bg-blue-900/30'
                              : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                          )}
                        >
                          <RefreshCw className={cn('w-3.5 h-3.5', isSyncingThis && 'animate-spin')} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
